import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { callApi } from "../api/api";
import {
  ArrowLeft,
  RefreshCw,
  Plus,
  Link2,
  Search,
  CheckCircle2,
  Shield,
  Building2,
  FileText,
  AlertCircle,
  X,
  Sparkles,
  Zap,
  Check,
  Grid,
  ArrowRightLeft,
  Activity,
  Cpu
} from "lucide-react";

const THEME = "rgb(37, 99, 235)";
const THEME_LIGHT = "rgba(37, 99, 235, 0.08)";

/* ─────────────────────────────────────────────────────────────
   SEARCHABLE SELECT COMPONENT WITH MULTI-SELECT & SMART ALIGNMENT
───────────────────────────────────────────────────────────── */
function SearchableSelect({
  options = [],       // [{ value, label, sub }]
  value,              // string or string[]
  onChange,
  placeholder = "Select…",
  disabled = false,
  loading = false,
  id,
  showSearch = false,
  prefixLabel = "",
  icon: Icon,
  isMapped = false,
  isMulti = false,    // Multi-select toggle support
  alignRight = false, // Right edge alignment (opens towards left)
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const optionsRefs = useRef([]);

  const multiValues = Array.isArray(value) ? value : (isMulti && value) ? [value] : [];
  const singleSelected = !isMulti ? options.find((o) => o.value === value) : null;

  const filtered = (showSearch && search.trim())
    ? options.filter(
      (o) =>
        (o.label || "").toLowerCase().includes(search.toLowerCase()) ||
        (o.sub && o.sub.toLowerCase().includes(search.toLowerCase()))
    )
    : options;

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open) {
      setFocusedIndex(0);
      if (showSearch && searchRef.current) {
        searchRef.current.focus();
      }
    } else {
      setFocusedIndex(-1);
    }
  }, [open, showSearch]);

  const handleToggle = () => {
    if (disabled || loading) return;
    setOpen((prev) => !prev);
    setSearch("");
  };

  const handleSelect = (val) => {
    if (isMulti) {
      let next;
      if (multiValues.includes(val)) {
        next = multiValues.filter((v) => v !== val);
      } else {
        next = [...multiValues, val];
      }
      onChange(next);
    } else {
      onChange(val);
      setOpen(false);
      setSearch("");
      if (triggerRef.current) {
        triggerRef.current.focus();
      }
    }
  };

  const handleKeyDown = (e) => {
    if (disabled || loading) return;

    const isInputElement = e.target && (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA");

    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || (e.key === " " && !isInputElement)) {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }

    if (e.key === " " && isInputElement) {
      return; // Allow space key typing in search input
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev + 1) % filtered.length);
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (filtered.length > 0) {
          setFocusedIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
        }
        break;
      case "Enter":
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filtered.length) {
          handleSelect(filtered[focusedIndex].value);
        } else if (filtered.length > 0) {
          handleSelect(filtered[0].value);
        }
        break;
      case "Space":
      case " ":
        if (!isInputElement) {
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < filtered.length) {
            handleSelect(filtered[focusedIndex].value);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setSearch("");
        if (triggerRef.current) {
          triggerRef.current.focus();
        }
        break;
      case "Tab":
        setOpen(false);
        setSearch("");
        break;
      default:
        break;
    }
  };

  const hasValue = isMulti ? multiValues.length > 0 : Boolean(value && value !== "");
  const activeMappedStyle = isMapped || hasValue;

  return (
    <div ref={wrapRef} onKeyDown={handleKeyDown} style={{ position: "relative", width: "100%", minWidth: "0", maxWidth: "100%", flex: "1 1 180px", boxSizing: "border-box", zIndex: open ? 1000 : 1 }} id={id}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled || loading}
        style={{
          ...ss.trigger,
          opacity: disabled ? 0.55 : 1,
          cursor: disabled || loading ? "not-allowed" : "pointer",
          borderColor: open ? THEME : activeMappedStyle ? "#93c5fd" : "#cbd5e1",
          background: activeMappedStyle ? "#eff6ff" : "#ffffff",
          boxShadow: open ? `0 0 0 3px ${THEME_LIGHT}` : "none",
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", overflow: "hidden", flex: 1 }}>
          {Icon && <Icon size={16} color={activeMappedStyle ? "#2563eb" : "#475569"} />}
          <span style={ss.triggerText}>
            {loading ? (
              "Loading…"
            ) : isMulti ? (
              multiValues.length > 0 ? (
                <span style={{ color: "#1e40af", fontWeight: "700" }}>
                  {multiValues.length} Selected ({options.filter((o) => multiValues.includes(o.value)).map((o) => o.label).join(", ")})
                </span>
              ) : (
                <span style={{ color: "#94a3b8" }}>{placeholder}</span>
              )
            ) : singleSelected ? (
              <span>
                {prefixLabel && <span style={{ color: "#64748b", fontWeight: "600", marginRight: "4px" }}>{prefixLabel}</span>}
                <strong style={{ color: activeMappedStyle ? "#1e40af" : "#0f172a", fontWeight: activeMappedStyle ? "700" : "500" }}>{singleSelected.label || singleSelected.value}</strong>
              </span>
            ) : value ? (
              <span style={{ color: activeMappedStyle ? "#1e40af" : "#0f172a", fontWeight: activeMappedStyle ? "700" : "500" }}>{value}</span>
            ) : (
              <span style={{ color: "#94a3b8" }}>{placeholder}</span>
            )}
          </span>
        </div>
        <svg
          style={{
            ...ss.chevron,
            color: activeMappedStyle ? "#2563eb" : "#64748b",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="5 8 10 13 15 8" />
        </svg>
      </button>

      {open && (
        <div style={ss.panel} role="listbox">
          {showSearch && (
            <div style={ss.searchWrap}>
              <Search size={15} color="#94a3b8" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search options…"
                style={ss.searchInput}
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  style={ss.clearBtn}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
          )}

          <div style={ss.optionList}>
            {filtered.length === 0 ? (
              <div style={ss.emptyMsg}>No results found</div>
            ) : (
              filtered.map((opt, index) => {
                const isSelected = isMulti
                  ? multiValues.includes(opt.value)
                  : value === opt.value;
                const isFocused = index === focusedIndex;

                return (
                  <button
                    key={opt.value}
                    ref={(el) => (optionsRefs.current[index] = el)}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(opt.value)}
                    onMouseEnter={() => setFocusedIndex(index)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "10px",
                      width: "100%",
                      padding: "8px 12px",
                      backgroundColor: isSelected ? "#eff6ff" : "#ffffff",
                      border: isSelected
                        ? "1.5px solid #2563eb"
                        : isFocused
                          ? "1.5px solid #93c5fd"
                          : "1.5px solid transparent",
                      color: isSelected || isFocused ? THEME : "#0f172a",
                      fontWeight: isSelected ? "600" : "400",
                      borderRadius: "8px",
                      marginBottom: "4px",
                      cursor: "pointer",
                      textAlign: "left",
                      boxSizing: "border-box",
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1, minWidth: 0, overflow: "hidden" }}>
                      {isMulti && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => { }}
                          style={{ cursor: "pointer", width: "15px", height: "15px", accentColor: "#2563eb", flexShrink: 0 }}
                        />
                      )}
                      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, overflow: "hidden", gap: "2px" }}>
                        <span style={{ fontSize: "12.5px", fontWeight: isSelected ? "700" : "600", color: isSelected ? "#1e40af" : "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {opt.label}
                        </span>
                        {opt.sub && (
                          <span style={{ fontSize: "10.5px", color: isSelected ? "#2563eb" : "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {opt.sub}
                          </span>
                        )}
                      </div>
                    </div>

                    {isSelected && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "20px", height: "20px", borderRadius: "50%", background: "#dbeafe", flexShrink: 0, marginLeft: "8px" }}>
                        <Check size={13} color="#1d4ed8" strokeWidth={2.5} />
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RoleDocumentMapping({ onBack }) {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const [mappings, setMappings] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  // Responsive state
  const [isMobile, setIsMobile] = useState(false);

  // Quick Pair Controls (1-to-Many Multi-Select: Select 1 Role -> Select N Document Types)
  const [quickRoleCode, setQuickRoleCode] = useState("");
  const [quickDocTypes, setQuickDocTypes] = useState([]);
  const [quickPairing, setQuickPairing] = useState(false);

  // Interactive Connection Canvas State
  const [activeTab, setActiveTab] = useState("canvas"); // "canvas" | "table"
  const [selectedRoleCode, setSelectedRoleCode] = useState(null);
  const [draggedRoleCode, setDraggedRoleCode] = useState(null);
  const [hoveredRole, setHoveredRole] = useState(null);
  const [hoveredDocType, setHoveredDocType] = useState(null);

  // SVG Line Coordinates state
  const [connectionPaths, setConnectionPaths] = useState([]);
  const boardRef = useRef(null);
  const leftNodesRef = useRef({});
  const rightNodesRef = useRef({});

  /* =========================================
     RESPONSIVE MEDIA QUERY
  ========================================= */
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 900px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const [initialLoading, setInitialLoading] = useState(true);

  /* =========================================
     FETCH DATA
  ========================================= */
  const fetchData = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) {
        setInitialLoading(true);
      }
      setError("");

      const [mappingsRes, docTypesRes, deptsRes, workflowRes] = await Promise.all([
        callApi(instance, accounts, "GET", `/api/assignments/role-mappings?_t=${Date.now()}`),
        callApi(instance, accounts, "GET", `/api/admin/lookup/document_type?_t=${Date.now()}`),
        callApi(instance, accounts, "GET", `/api/departments?_t=${Date.now()}`),
        callApi(instance, accounts, "GET", `/api/assignments/workflow-orders?_t=${Date.now()}`),
      ]);

      const loadedMappings = mappingsRes?.data?.data || (Array.isArray(mappingsRes?.data) ? mappingsRes.data : []);
      const loadedDocTypes = docTypesRes?.data?.data || (Array.isArray(docTypesRes?.data) ? docTypesRes.data : []);
      const loadedDepts = deptsRes?.data?.data || (Array.isArray(deptsRes?.data) ? deptsRes.data : []);
      const workflowData = workflowRes?.data?.data || (Array.isArray(workflowRes?.data) ? workflowRes.data : workflowRes?.data) || {};
      const activePipelineRoles = Array.isArray(workflowData?.assignedRoles) ? workflowData.assignedRoles : [];
      const activeRoleCodesSet = new Set(activePipelineRoles.map((r) => r.roleCode));

      const rawMappings = Array.isArray(loadedMappings) ? loadedMappings : [];
      setMappings(rawMappings.filter((m) => !m.isDeleted && m.isActive !== false && m.is_active !== 0));
      setDocumentTypes(Array.isArray(loadedDocTypes) ? loadedDocTypes : []);

      const deptList = Array.isArray(loadedDepts) ? loadedDepts : [];
      const availableDeptList = activeRoleCodesSet.size > 0
        ? deptList.filter((d) => !activeRoleCodesSet.has(d.department_code || d.departmentCode))
        : deptList;

      const deptAsRoles = availableDeptList.map((d) => ({
        role_id: d.department_id || d.departmentId,
        role_code: d.department_code || d.departmentCode,
        role_name: d.department_name || d.departmentName,
      }));
      setRoles(deptAsRoles);
    } catch (err) {
      console.error("Failed to load 1:N role mappings:", err);
      setError(err?.response?.data?.message || err.message || "Failed to load document role mappings.");
    } finally {
      setInitialLoading(false);
      setLoading(false);
      setRefreshing(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  /* =========================================
     LOOKUP MAPS FOR 1-TO-MANY (1 ROLE -> N DOC TYPES)
  ========================================= */
  const roleToMappingsMap = React.useMemo(() => {
    const map = {};
    mappings.forEach((m) => {
      if (m.roleCode) {
        if (!map[m.roleCode]) map[m.roleCode] = [];
        map[m.roleCode].push(m);
      }
    });
    return map;
  }, [mappings]);

  const docToMappingMap = React.useMemo(() => {
    const map = {};
    mappings.forEach((m) => {
      if (m.documentType) map[m.documentType] = m;
    });
    return map;
  }, [mappings]);

  /* =========================================
     SVG FAN-OUT CABLE RECALCULATION (1 ROLE -> N DOC TYPES)
  ========================================= */
  const updatePaths = useCallback(() => {
    if (!boardRef.current || activeTab !== "canvas" || isMobile) return;

    const boardRect = boardRef.current.getBoundingClientRect();
    const newPaths = [];

    mappings.forEach((m) => {
      const leftEl = leftNodesRef.current[m.roleCode];
      const rightEl = rightNodesRef.current[m.documentType];

      if (leftEl && rightEl) {
        const leftRect = leftEl.getBoundingClientRect();
        const rightRect = rightEl.getBoundingClientRect();

        const x1 = leftRect.right - boardRect.left;
        const y1 = leftRect.top + leftRect.height / 2 - boardRect.top;

        const x2 = rightRect.left - boardRect.left;
        const y2 = rightRect.top + rightRect.height / 2 - boardRect.top;

        const dx = Math.abs(x2 - x1) * 0.45;
        const pathData = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;

        newPaths.push({
          id: m.mappingId,
          roleCode: m.roleCode,
          docCode: m.documentType,
          roleName: m.roleName || m.roleCode,
          docName: m.documentTypeName || m.documentType,
          pathData,
          x1,
          y1,
          x2,
          y2,
          isActive: m.isActive,
        });
      }
    });

    setConnectionPaths(newPaths);
  }, [mappings, activeTab, isMobile]);

  useEffect(() => {
    updatePaths();
    window.addEventListener("resize", updatePaths);
    const timer1 = setTimeout(updatePaths, 150);
    const timer2 = setTimeout(updatePaths, 500);

    return () => {
      window.removeEventListener("resize", updatePaths);
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, [updatePaths, mappings, activeTab, isMobile, roles, documentTypes]);

  const [pendingReassignment, setPendingReassignment] = useState(null);

  /* =========================================
     EXECUTE 1-TO-MANY MULTI PAIR & CONNECT
  ========================================= */
  const connectRoleToDoc = async (roleCode, docInput, bypassConfirmation = false) => {
    if (!roleCode || !docInput) return;
    const docList = Array.isArray(docInput) ? docInput : [docInput];
    if (docList.length === 0) return;

    if (!bypassConfirmation) {
      const conflicts = docList
        .map((docCode) => {
          const existing = docToMappingMap[docCode];
          if (existing && existing.roleCode !== roleCode) {
            const docObj = documentTypes.find((d) => d.lookup_code === docCode);
            const targetRoleObj = roles.find((r) => r.role_code === roleCode);
            return {
              docCode,
              docName: docObj?.lookup_name || docCode,
              currentRoleCode: existing.roleCode,
              currentRoleName: existing.roleName || existing.roleCode,
              targetRoleCode: roleCode,
              targetRoleName: targetRoleObj?.role_name || roleCode,
            };
          }
          return null;
        })
        .filter(Boolean);

      if (conflicts.length > 0) {
        setPendingReassignment({ roleCode, docInput, conflicts });
        return;
      }
    }

    try {
      setQuickPairing(true);

      await Promise.all(
        docList.map((docTypeCode) =>
          callApi(instance, accounts, "POST", "/api/assignments/role-mappings", {
            roleCode: roleCode,
            documentType: docTypeCode,
            isActive: true,
          })
        )
      );

      const roleObj = roles.find((r) => r.role_code === roleCode);
      const roleName = roleObj?.role_name || roleCode;

      showToast(`⚡ Mapped ${docList.length} Document Type(s) to Role "${roleName}".`, "success");
      setSelectedRoleCode(null);
      setDraggedRoleCode(null);
      setQuickRoleCode("");
      setQuickDocTypes([]);
      setPendingReassignment(null);
      fetchData();
    } catch (err) {
      console.error("Connect error:", err);
      showToast(err?.response?.data?.message || err.message || "Failed to add 1:N rule.", "error");
    } finally {
      setQuickPairing(false);
    }
  };

  /* =========================================
     ROLE CARD FOOTER MULTI-SELECT HANDLER (CHECK & UNCHECK TOGGLE)
  ========================================= */
  const handleCardMultiSelect = async (roleCode, nextSelectedArray, bypassConfirmation = false) => {
    const currentMappings = roleToMappingsMap[roleCode] || [];
    const currentDocCodes = currentMappings.map((m) => m.documentType);

    const addedDocCodes = nextSelectedArray.filter((code) => !currentDocCodes.includes(code));
    const removedMappings = currentMappings.filter((m) => !nextSelectedArray.includes(m.documentType));

    if (!bypassConfirmation && addedDocCodes.length > 0) {
      const conflicts = addedDocCodes
        .map((docCode) => {
          const existing = docToMappingMap[docCode];
          if (existing && existing.roleCode !== roleCode) {
            const docObj = documentTypes.find((d) => d.lookup_code === docCode);
            const targetRoleObj = roles.find((r) => r.role_code === roleCode);
            return {
              docCode,
              docName: docObj?.lookup_name || docCode,
              currentRoleCode: existing.roleCode,
              currentRoleName: existing.roleName || existing.roleCode,
              targetRoleCode: roleCode,
              targetRoleName: targetRoleObj?.role_name || roleCode,
            };
          }
          return null;
        })
        .filter(Boolean);

      if (conflicts.length > 0) {
        setPendingReassignment({ roleCode, docInput: nextSelectedArray, conflicts, isCardMultiSelect: true });
        return;
      }
    }

    try {
      setQuickPairing(true);
      if (addedDocCodes.length > 0) {
        await Promise.all(
          addedDocCodes.map((code) =>
            callApi(instance, accounts, "POST", "/api/assignments/role-mappings", {
              roleCode: roleCode,
              documentType: code,
              isActive: true,
            })
          )
        );
      }

      if (removedMappings.length > 0) {
        await Promise.all(
          removedMappings.map((m) =>
            callApi(instance, accounts, "DELETE", `/api/assignments/role-mappings/${m.mappingId}`)
          )
        );
      }

      const roleObj = roles.find((r) => r.role_code === roleCode);
      showToast(`Updated document assignments for Role "${roleObj?.role_name || roleCode}".`, "success");
      setPendingReassignment(null);
      fetchData();
    } catch (err) {
      console.error("Card multi select error:", err);
      showToast(err?.response?.data?.message || err.message || "Failed to update role assignments.", "error");
    } finally {
      setQuickPairing(false);
    }
  };

  const [pendingUnlink, setPendingUnlink] = useState(null);
  const [unlinking, setUnlinking] = useState(false);

  const handleDisconnect = (e, mappingId, docLabel, roleLabel) => {
    e?.stopPropagation();
    setPendingUnlink({ mappingId, docLabel, roleLabel });
  };

  const executeUnlink = async () => {
    if (!pendingUnlink) return;
    const { mappingId, docLabel } = pendingUnlink;

    try {
      setUnlinking(true);
      await callApi(instance, accounts, "DELETE", `/api/assignments/role-mappings/${mappingId}`);
      showToast(`Unlinked "${docLabel}".`, "info");
      setPendingUnlink(null);
      fetchData();
    } catch (err) {
      console.error("Delete mapping error:", err);
      showToast(err?.response?.data?.message || err.message || "Failed to remove mapping.", "error");
    } finally {
      setUnlinking(false);
    }
  };

  /* =========================================
     DRAG AND DROP HANDLERS (LEFT ROLE -> RIGHT DOC TYPE)
  ========================================= */
  const handleDragStart = (e, role) => {
    setDraggedRoleCode(role.role_code);
    e.dataTransfer.setData("text/plain", role.role_code);
    e.dataTransfer.effectAllowed = "link";
  };

  const handleDragOver = (e, docCode) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "link";
    if (hoveredDocType !== docCode) {
      setHoveredDocType(docCode);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setHoveredDocType(null);
  };

  const handleDrop = (e, docCode) => {
    e.preventDefault();
    setHoveredDocType(null);
    const rCode = e.dataTransfer.getData("text/plain") || draggedRoleCode;
    if (rCode && docCode) {
      connectRoleToDoc(rCode, docCode);
    }
  };

  /* =========================================
     FILTERED LISTS & DROPDOWN OPTIONS
  ========================================= */
  const roleOptions = roles.map((r) => {
    const count = (roleToMappingsMap[r.role_code] || []).length;
    return {
      value: r.role_code,
      label: r.role_name,
      sub: count > 0 ? `✔ ${count} Doc Types` : "Unassigned",
    };
  });

  const docOptions = documentTypes.map((dt) => {
    const mapping = docToMappingMap[dt.lookup_code];
    return {
      value: dt.lookup_code,
      label: dt.lookup_name,
      sub: mapping ? `✔ Mapped to ${mapping.roleName || mapping.roleCode}` : "",
    };
  });

  const filteredRoles = roles.filter((r) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      (r.role_name || "").toLowerCase().includes(q) ||
      (r.role_code || "").toLowerCase().includes(q)
    );
  });

  const filteredDocTypes = documentTypes.filter((d) => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    return (
      (d.lookup_name || "").toLowerCase().includes(q) ||
      (d.lookup_code || "").toLowerCase().includes(q)
    );
  });

  const mappedDocTypesCount = Object.keys(docToMappingMap).length;
  const coveragePercent = documentTypes.length > 0 ? Math.round((mappedDocTypesCount / documentTypes.length) * 100) : 0;

  return (
    <div style={s.page} className="smooth-fade-in">
      <style>{`
        @keyframes spin-icon-anim {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes smoothFadeIn {
          from { opacity: 0.85; transform: scale(0.998); }
          to { opacity: 1; transform: scale(1); }
        }
        .spin-icon {
          animation: spin-icon-anim 0.75s linear infinite;
        }
        .smooth-fade-in {
          animation: smoothFadeIn 0.3s ease-out;
        }
        .confirm-btn:hover {
          background: #1d4ed8 !important;
          box-shadow: 0 4px 14px rgba(37,99,235,0.4) !important;
          transform: translateY(-1px);
        }
        .confirm-btn:disabled {
          background: #2563eb !important;
          opacity: 0.85 !important;
          cursor: wait !important;
          transform: none !important;
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .spin-icon {
          animation: spin 0.8s linear infinite;
        }
      `}</style>

      {/* TOAST NOTIFICATION */}
      {toast && (
        <div style={s.toastNotification(toast.type)}>
          {toast.type === "success" ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* HEADER */}
      <div style={s.header}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px", flexWrap: "wrap", gap: "12px" }}>
          <div>
            <div style={s.breadcrumb}>
              <span>Administration</span> &gt;{" "}
              <span
                onClick={onBack || (() => navigate("/manage"))}
                style={{ color: "#2563eb", fontWeight: "600", cursor: "pointer" }}
                onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                title="Click to go to Manage Menu"
              >
                Manage
              </span>{" "}
              &gt; <span style={{ color: "#0f172a", fontWeight: "600" }}>Department &amp; Document Type Mapping</span>
            </div>
            <h1 style={s.title}>Department &amp; Document Type Mapping</h1>
          </div>

          <div style={s.headerActions}>
            <button style={s.refreshBtn} onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw size={13} className={refreshing ? "spin-icon" : ""} />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>
      </div>

      {/* SAME ROW: TABS & SEARCH TOOLBAR */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", marginBottom: "20px", flexWrap: "wrap" }}>
        <div style={s.tabGroup}>
          <button
            style={s.tabBtn(activeTab === "canvas")}
            onClick={() => setActiveTab("canvas")}
          >
            <Sparkles size={14} /> Connection Board
          </button>
          <button
            style={s.tabBtn(activeTab === "table")}
            onClick={() => setActiveTab("table")}
          >
            <Grid size={14} /> Department Cards Grid ({roles.length})
          </button>
        </div>

        <div style={{ ...s.searchBox, maxWidth: "420px", flex: "1 1 300px" }}>
          <Search size={16} color="#94a3b8" />
          <input
            type="text"
            placeholder="Search User Roles or Document Types..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={s.searchInput}
          />
        </div>

        {selectedRoleCode && (
          <div style={{ ...s.selectedBanner, width: "100%", marginTop: "6px" }}>
            <span>Active Left Role: <strong>{selectedRoleCode}</strong>. Click any Document Type on the Right to assign!</span>
            <button style={s.clearSelectedBtn} onClick={() => setSelectedRoleCode(null)}>Cancel</button>
          </div>
        )}
      </div>

      {/* ERROR ALERT */}
      {error && (
        <div style={s.errorAlert}>
          <AlertCircle size={16} color="#dc2626" />
          <span>{error}</span>
        </div>
      )}

      {/* TAB 1: CONNECTION BOARD (1-TO-MANY FAN-OUT) */}
      {activeTab === "canvas" && (
        <div style={{ ...s.canvasBoard(isMobile), position: "relative" }} ref={boardRef}>
          {refreshing && (
            <div style={s.overlayLoader}>
              <RefreshCw size={20} color={THEME} className="spin-icon" />
              <span>Updating connections board…</span>
            </div>
          )}
          {!isMobile && (
            <svg style={s.svgLayer}>
              <defs>
                <linearGradient id="cableGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#2563eb" stopOpacity="0.85" />
                  <stop offset="50%" stopColor="#8b5cf6" stopOpacity="1" />
                  <stop offset="100%" stopColor="#10b981" stopOpacity="0.85" />
                </linearGradient>

                <filter id="glowEffect" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="3.5" result="blur" />
                  <feComposite in="SourceGraphic" in2="blur" operator="over" />
                </filter>
              </defs>

              {connectionPaths.map((p) => {
                const isHovered = hoveredRole === p.roleCode || hoveredDocType === p.docCode;
                return (
                  <g key={p.id}>
                    <path
                      d={p.pathData}
                      fill="none"
                      stroke={isHovered ? "#818cf8" : "url(#cableGrad)"}
                      strokeWidth={isHovered ? 5 : 2.5}
                      opacity={isHovered ? 1 : 0.75}
                      filter={isHovered ? "url(#glowEffect)" : undefined}
                      style={{ transition: "all 0.2s ease" }}
                    />
                    <circle cx={p.x1} cy={p.y1} r={4.5} fill="#2563eb" />
                    <circle cx={p.x2} cy={p.y2} r={4.5} fill="#10b981" />
                  </g>
                );
              })}
            </svg>
          )}

          <div style={s.columnsGrid(isMobile)}>
            {/* LEFT COLUMN: DEPARTMENTS */}
            <div style={s.column}>
              <div style={s.columnHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={s.columnHeaderBadge("#2563eb")}>
                    <Building2 size={14} color="#2563eb" />
                  </div>
                  <div>
                    <h3 style={s.columnTitle}>Departments ({filteredRoles.length})</h3>
                    <span style={s.columnSubtitle}>Left Source Node (Click or Drag)</span>
                  </div>
                </div>
              </div>

              <div style={s.nodesListFullHeight}>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={`l-node-skel-${i}`} style={{ ...s.docNodeCard(false, false, false), opacity: 0.85, padding: "12px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                        <div style={{ ...s.skeletonBar, width: "120px", height: "14px" }} />
                        <div style={{ ...s.skeletonBar, width: "50px", height: "14px", borderRadius: "10px" }} />
                      </div>
                      <div style={{ ...s.skeletonBar, width: "70px", height: "10px" }} />
                    </div>
                  ))
                ) : (
                  filteredRoles.map((role) => {
                    const roleMappings = roleToMappingsMap[role.role_code] || [];
                    const isSelected = selectedRoleCode === role.role_code;
                    const isDraggingThis = draggedRoleCode === role.role_code;
                    const hasConnected = roleMappings.length > 0;

                    return (
                      <div
                        key={role.role_code || role.role_id}
                        ref={(el) => (leftNodesRef.current[role.role_code] = el)}
                        draggable={true}
                        onDragStart={(e) => handleDragStart(e, role)}
                        onDragEnd={() => setDraggedRoleCode(null)}
                        onMouseEnter={() => setHoveredRole(role.role_code)}
                        onMouseLeave={() => setHoveredRole(null)}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedRoleCode(null);
                          } else {
                            setSelectedRoleCode(role.role_code);
                          }
                        }}
                        style={s.docNodeCard(isSelected, hasConnected, isDraggingThis)}
                      >
                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                          <div>
                            <div style={s.nodeName}>{role.role_name}</div>
                            <div style={s.nodeCode}>{role.role_code}</div>
                          </div>
                          <div>
                            {hasConnected ? (
                              <span style={s.mappedBadge}>
                                <Check size={10} /> {roleMappings.length} Doc Types
                              </span>
                            ) : (
                              <span style={s.unmappedBadge}>Unassigned</span>
                            )}
                          </div>
                        </div>

                        {/* TAG CHIPS OF CONNECTED DOCUMENT TYPES */}
                        {hasConnected && (
                          <div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                            {roleMappings.map((m) => (
                              <div key={m.mappingId} style={s.docChip}>
                                <FileText size={11} color="#2563eb" />
                                <span>{m.documentTypeName || m.documentType}</span>
                                <button
                                  style={s.chipRemoveBtn}
                                  onClick={(e) => handleDisconnect(e, m.mappingId, m.documentTypeName || m.documentType, role.role_name)}
                                  title="Remove document type"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {!isMobile && <div style={s.handlePinRight(hasConnected)} />}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: DOCUMENT TYPES */}
            <div style={s.column}>
              <div style={s.columnHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={s.columnHeaderBadge("#10b981")}>
                    <FileText size={14} color="#10b981" />
                  </div>
                  <div>
                    <h3 style={s.columnTitle}>Document Ingestion Types ({filteredDocTypes.length})</h3>
                    <span style={s.columnSubtitle}>Right Target Node (Drop target)</span>
                  </div>
                </div>
              </div>

              <div style={s.nodesListFullHeight}>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div key={`r-node-skel-${i}`} style={{ ...s.roleNodeCard(false, false, false), opacity: 0.85, padding: "12px 14px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                        <div style={{ ...s.skeletonBar, width: "130px", height: "14px" }} />
                        <div style={{ ...s.skeletonBar, width: "60px", height: "14px", borderRadius: "10px" }} />
                      </div>
                      <div style={{ ...s.skeletonBar, width: "80px", height: "10px" }} />
                    </div>
                  ))
                ) : (
                  filteredDocTypes.map((dt) => {
                    const isHovered = hoveredDocType === dt.lookup_code;
                    const connectedMapping = docToMappingMap[dt.lookup_code];

                    return (
                      <div
                        key={dt.lookup_code || dt.lookup_id}
                        ref={(el) => (rightNodesRef.current[dt.lookup_code] = el)}
                        onDragOver={(e) => handleDragOver(e, dt.lookup_code)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, dt.lookup_code)}
                        onMouseEnter={() => setHoveredDocType(dt.lookup_code)}
                        onMouseLeave={() => setHoveredDocType(null)}
                        onClick={() => {
                          if (selectedRoleCode) {
                            connectRoleToDoc(selectedRoleCode, dt.lookup_code);
                          }
                        }}
                        style={s.roleNodeCard(isHovered, Boolean(selectedRoleCode), Boolean(connectedMapping))}
                      >
                        {!isMobile && <div style={s.handlePinLeft(Boolean(connectedMapping))} />}

                        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                          <div>
                            <div style={s.nodeName}>{dt.lookup_name}</div>
                            <div style={s.nodeCode}>{dt.lookup_code}</div>
                          </div>
                          <span style={s.roleCountBadge(Boolean(connectedMapping))}>
                            {connectedMapping ? "Assigned" : "Unmapped"}
                          </span>
                        </div>

                        {connectedMapping ? (
                          <div style={s.connectedSingleDocBox}>
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: "600", color: "#1e40af" }}>
                              <Shield size={13} color="#2563eb" />
                              <span>{connectedMapping.roleName || connectedMapping.roleCode}</span>
                            </div>
                            <button
                              style={s.tagUnlinkBtn}
                              onClick={(e) => handleDisconnect(e, connectedMapping.mappingId, dt.lookup_name, connectedMapping.roleName)}
                              title="Unlink mapping"
                            >
                              <X size={12} color="#dc2626" />
                            </button>
                          </div>
                        ) : (
                          <div style={s.unassignedHint}>
                            <span>Click Left Role or drag to assign</span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: EXECUTIVE ROLE CARDS GRID VIEW */}
      {activeTab === "table" && (
        <div style={s.cardsGridContainer}>
          {loading ? (
            <div style={s.rolesCardsGrid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={`tab2-skel-${i}`} style={{ ...s.roleGridCard(false), opacity: 0.85 }}>
                  <div style={s.cardHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={s.cardHeaderIconBox(false)}>
                        <div style={{ ...s.skeletonBar, width: "18px", height: "18px", borderRadius: "4px" }} />
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                        <div style={{ ...s.skeletonBar, width: "130px", height: "16px" }} />
                        <div style={{ ...s.skeletonBar, width: "70px", height: "10px" }} />
                      </div>
                    </div>
                    <div style={{ ...s.skeletonBar, width: "60px", height: "18px", borderRadius: "10px" }} />
                  </div>
                  <div style={{ padding: "16px 0", display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ ...s.skeletonBar, width: "100px", height: "10px" }} />
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <div style={{ ...s.skeletonBar, width: "90px", height: "24px", borderRadius: "8px" }} />
                      <div style={{ ...s.skeletonBar, width: "110px", height: "24px", borderRadius: "8px" }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredRoles.length === 0 ? (
            <div style={s.emptyCardsMsg}>No User Roles match your search criteria.</div>
          ) : (
            <div style={s.rolesCardsGrid}>
              {filteredRoles.map((role, index) => {
                const roleMappings = roleToMappingsMap[role.role_code] || [];
                const hasConnected = roleMappings.length > 0;
                const assignedDocCodes = roleMappings.map((m) => m.documentType);
                const isRightColumn = index % 2 !== 0;

                return (
                  <div key={role.role_code} style={s.roleGridCard(hasConnected)}>
                    {/* CARD HEADER */}
                    <div style={s.cardHeader}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={s.cardHeaderIconBox(hasConnected)}>
                          <Shield size={18} color={hasConnected ? "#2563eb" : "#64748b"} />
                        </div>
                        <div>
                          <h3 style={s.cardRoleTitle}>{role.role_name}</h3>
                          <div style={s.cardRoleCode}>{role.role_code}</div>
                        </div>
                      </div>

                      {hasConnected ? (
                        <span style={s.cardBadgeActive}>
                          <Zap size={12} /> {roleMappings.length} Mapped
                        </span>
                      ) : (
                        <span style={s.cardBadgeUnmapped}>Unassigned</span>
                      )}
                    </div>

                    {/* CARD BODY: ASSIGNED DOCUMENT TYPES CHIPS */}
                    <div style={s.cardBody}>
                      <div style={s.cardSectionLabel}>
                        ASSIGNED DOCUMENT TYPES ({roleMappings.length})
                      </div>

                      {hasConnected ? (
                        <div style={s.cardChipsContainer}>
                          {roleMappings.map((m) => (
                            <div key={m.mappingId} style={s.cardDocChip}>
                              <FileText size={13} color="#2563eb" />
                              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {m.documentTypeName || m.documentType}
                              </span>
                              <button
                                style={s.chipRemoveBtn}
                                onClick={(e) => handleDisconnect(e, m.mappingId, m.documentTypeName || m.documentType, role.role_name)}
                                title="Remove document type from role"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={s.cardEmptyDocsMsg}>
                          No document types mapped to this role yet.
                        </div>
                      )}
                    </div>

                    {/* CARD FOOTER: INTERACTIVE MULTI-SELECT CHECKBOX DROPDOWN WITH SMART ALIGNMENT */}
                    <div style={s.cardFooter}>
                      <SearchableSelect
                        options={documentTypes.map((dt) => ({
                          value: dt.lookup_code,
                          label: `${dt.lookup_name} (${dt.lookup_code})`,
                          sub: docToMappingMap[dt.lookup_code] ? `✔ Mapped to ${docToMappingMap[dt.lookup_code].roleName}` : ""
                        }))}
                        value={assignedDocCodes}
                        onChange={(val) => handleCardMultiSelect(role.role_code, val)}
                        placeholder="Assign Document Type(s) to Role..."
                        icon={Plus}
                        isMulti={true}
                        showSearch={true}
                        alignRight={isRightColumn}
                        isMapped={hasConnected}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* REASSIGNMENT CONFIRMATION MODAL FOR ALREADY CONNECTED DOC TYPES */}
      {pendingReassignment && (
        <div style={s.modalOverlay}>
          <div style={s.modalBox}>
            <div style={s.modalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", padding: "8px", borderRadius: "10px", color: "#d48806", display: "flex" }}>
                  <AlertCircle size={22} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "800", color: "#0f172a" }}>
                    Document Type Reassignment Warning
                  </h3>
                  <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                    Selected document type is already connected to another user role
                  </p>
                </div>
              </div>
              <button style={s.closeModalBtn} onClick={() => setPendingReassignment(null)}>
                <X size={16} />
              </button>
            </div>

            <div style={s.modalBody}>
              <div style={{ fontSize: "13px", color: "#334155", marginBottom: "12px", fontWeight: "600" }}>
                The following document type mapping(s) will be transferred:
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px", maxHeight: "260px", overflowY: "auto", marginBottom: "16px" }}>
                {pendingReassignment.conflicts.map((c) => (
                  <div key={c.docCode} style={s.conflictCard}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <FileText size={16} color="#2563eb" />
                      <span style={{ fontSize: "14px", fontWeight: "800", color: "#0f172a" }}>
                        {c.docName}
                      </span>
                      <span style={{ fontSize: "11px", color: "#64748b", fontFamily: "monospace", background: "#e2e8f0", padding: "2px 6px", borderRadius: "4px" }}>
                        {c.docCode}
                      </span>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "12px", background: "#ffffff", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "10px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>Currently Connected To</div>
                        <div style={{ fontWeight: "700", color: "#dc2626", marginTop: "2px" }}>
                          🛡️ {c.currentRoleName} ({c.currentRoleCode})
                        </div>
                      </div>

                      <ArrowRightLeft size={16} color="#94a3b8" />

                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "10px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>New Target Role</div>
                        <div style={{ fontWeight: "700", color: "#2563eb", marginTop: "2px" }}>
                          🛡️ {c.targetRoleName} ({c.targetRoleCode})
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={s.warningCallout}>
                <AlertCircle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: "2px" }} />
                <span>
                  <strong>Are you sure you want to proceed?</strong> Reassigning will disconnect these document types from their current roles and route future intake items to the new target role.
                </span>
              </div>
            </div>

            <div style={s.modalFooter}>
              <button
                type="button"
                style={s.cancelModalBtn}
                disabled={quickPairing}
                onClick={() => setPendingReassignment(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="confirm-btn"
                style={s.confirmReassignBtn(quickPairing)}
                disabled={quickPairing}
                onClick={() => {
                  const { roleCode, docInput, isCardMultiSelect } = pendingReassignment;
                  if (isCardMultiSelect) {
                    handleCardMultiSelect(roleCode, docInput, true);
                  } else {
                    connectRoleToDoc(roleCode, docInput, true);
                  }
                }}
              >
                {quickPairing ? (
                  <>
                    <RefreshCw size={14} className="spin-icon" />
                    <span>Reassigning...</span>
                  </>
                ) : (
                  <>
                    <Zap size={14} />
                    <span>Confirm Reassignment</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* UNLINK / DISCONNECT CONFIRMATION MODAL */}
      {pendingUnlink && (
        <div style={s.modalOverlay}>
          <div style={s.modalBox}>
            <div style={s.modalHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", padding: "8px", borderRadius: "10px", color: "#dc2626", display: "flex" }}>
                  <AlertCircle size={22} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "800", color: "#0f172a" }}>
                    Unlink Document Type Warning
                  </h3>
                  <p style={{ margin: "2px 0 0 0", fontSize: "12px", color: "#64748b" }}>
                    Are you sure you want to disconnect this document type from the role?
                  </p>
                </div>
              </div>
              <button style={s.closeModalBtn} onClick={() => setPendingUnlink(null)}>
                <X size={16} />
              </button>
            </div>

            <div style={s.modalBody}>
              <div style={{ fontSize: "13px", color: "#334155", marginBottom: "12px", fontWeight: "600" }}>
                The following document type connection will be unlinked:
              </div>

              <div style={s.conflictCard}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                  <FileText size={16} color="#2563eb" />
                  <span style={{ fontSize: "14px", fontWeight: "800", color: "#0f172a" }}>
                    {pendingUnlink.docLabel}
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: "10px", fontSize: "12px", background: "#ffffff", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "10px", color: "#64748b", fontWeight: "700", textTransform: "uppercase" }}>Currently Linked To Role</div>
                    <div style={{ fontWeight: "700", color: "#dc2626", marginTop: "2px" }}>
                      🛡️ {pendingUnlink.roleLabel}
                    </div>
                  </div>
                </div>
              </div>

              <div style={s.warningCallout}>
                <AlertCircle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: "2px" }} />
                <span>
                  <strong>Warning:</strong> Unlinking will detach <strong>"{pendingUnlink.docLabel}"</strong> from <strong>"{pendingUnlink.roleLabel}"</strong>. Future intake documents of this type will remain unassigned until mapped to another role.
                </span>
              </div>
            </div>

            <div style={s.modalFooter}>
              <button
                type="button"
                style={s.cancelModalBtn}
                disabled={unlinking}
                onClick={() => setPendingUnlink(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-btn"
                style={s.dangerModalBtn(unlinking)}
                disabled={unlinking}
                onClick={executeUnlink}
              >
                {unlinking ? (
                  <>
                    <RefreshCw size={14} className="spin-icon" />
                    <span>Unlinking...</span>
                  </>
                ) : (
                  <>
                    <X size={14} />
                    <span>Unlink "{pendingUnlink.docLabel}"</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ss = {
  trigger: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "10px 13px",
    border: "1px solid #cbd5e1",
    borderRadius: "9px",
    fontSize: "14px",
    background: "#ffffff",
    color: "#0f172a",
    fontFamily: "inherit",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color 0.15s, box-shadow 0.15s",
    height: "44px",
    boxSizing: "border-box",
    gap: "8px",
  },
  triggerText: {
    flex: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "14px",
  },
  triggerSub: {
    color: "#94a3b8",
    fontSize: "12px",
  },
  chevron: {
    width: 16,
    height: 16,
    flexShrink: 0,
    color: "#64748b",
    transition: "transform 0.2s ease",
  },
  panel: {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    width: "100%",
    maxWidth: "100%",
    zIndex: 9999,
    background: "#ffffff",
    border: "1.5px solid #cbd5e1",
    borderRadius: "12px",
    boxShadow: "0 10px 30px rgba(15,23,42,0.14)",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  searchWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    borderBottom: "1px solid #f1f5f9",
    background: "#f8fafc",
  },
  searchInput: {
    flex: 1,
    border: "none",
    background: "transparent",
    outline: "none",
    fontSize: "13px",
    color: "#0f172a",
    fontFamily: "inherit",
  },
  clearBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94a3b8",
    fontSize: "18px",
    lineHeight: 1,
    padding: "0 2px",
    display: "flex",
    alignItems: "center",
  },
  optionList: {
    maxHeight: "240px",
    overflowY: "auto",
    padding: "6px",
  },
  option: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    width: "100%",
    padding: "9px 12px",
    borderRadius: "7px",
    border: "none",
    cursor: "pointer",
    fontSize: "13px",
    fontFamily: "inherit",
    textAlign: "left",
    transition: "background 0.1s",
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  },
  optionLabel: {
    flex: 1,
    fontSize: "13px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionSub: {
    fontSize: "11px",
    color: "#64748b",
    marginLeft: "8px",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  checkIcon: {
    width: 16,
    height: 16,
    flexShrink: 0,
  },
  emptyMsg: {
    padding: "16px 12px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "13px",
  },
};

const s = {
  fullLoadingBox: {
    padding: "100px 20px",
    textAlign: "center",
    color: "#475569",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    background: "#ffffff",
    borderRadius: "16px",
    border: "1.5px solid #cbd5e1",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(15, 23, 42, 0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999999,
    padding: "20px",
  },
  modalBox: {
    background: "#ffffff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "540px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    border: "1px solid #cbd5e1",
    overflow: "hidden",
  },
  modalHeader: {
    padding: "16px 20px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#fafafa",
  },
  closeModalBtn: {
    background: "none",
    border: "none",
    color: "#64748b",
    cursor: "pointer",
    padding: "4px",
    borderRadius: "6px",
    display: "flex",
  },
  modalBody: {
    padding: "20px",
  },
  conflictCard: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    padding: "12px",
  },
  warningCallout: {
    background: "#fffbeb",
    border: "1px solid #fef3c7",
    borderRadius: "10px",
    padding: "12px",
    fontSize: "12px",
    color: "#92400e",
    display: "flex",
    gap: "10px",
    alignItems: "flex-start",
  },
  modalFooter: {
    padding: "14px 20px",
    borderTop: "1px solid #f1f5f9",
    background: "#fafafa",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
  },
  cancelModalBtn: {
    padding: "8px 16px",
    borderRadius: "8px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#475569",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
  },
  confirmReassignBtn: (isProcessing) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 18px",
    borderRadius: "8px",
    border: "none",
    background: isProcessing ? "#1d4ed8" : "#2563eb",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "700",
    cursor: isProcessing ? "wait" : "pointer",
    opacity: isProcessing ? 0.85 : 1,
    boxShadow: isProcessing ? "none" : "0 4px 12px rgba(37,99,235,0.25)",
    transition: "all 0.15s ease",
  }),
  dangerModalBtn: (isProcessing) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 18px",
    borderRadius: "8px",
    border: "none",
    background: isProcessing ? "#b91c1c" : "#dc2626",
    color: "#ffffff",
    fontSize: "13px",
    fontWeight: "700",
    cursor: isProcessing ? "wait" : "pointer",
    opacity: isProcessing ? 0.85 : 1,
    boxShadow: isProcessing ? "none" : "0 4px 12px rgba(220,38,38,0.25)",
    transition: "all 0.15s ease",
  }),
  loadingBox: {
    padding: "60px 20px",
    textAlign: "center",
    color: "#475569",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    boxSizing: "border-box",
  },
  page: {
    padding: "0 clamp(10px, 3vw, 24px) clamp(10px, 3vw, 24px) clamp(10px, 3vw, 24px)",
    boxSizing: "border-box",
    width: "100%",
  },
  header: {
    marginBottom: "18px",
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "12px",
    color: THEME,
    cursor: "pointer",
    fontWeight: "600",
    background: "none",
    border: "none",
    padding: 0,
    marginBottom: "2px",
    outline: "none",
  },
  breadcrumb: {
    fontSize: "12px",
    color: "#94a3b8",
    marginBottom: "4px",
  },
  title: {
    margin: 0,
    fontSize: "20px",
    fontWeight: "800",
    color: "#0f172a",
    letterSpacing: "-0.4px",
    lineHeight: "1.2",
  },
  sub: {
    color: "#64748b",
    fontSize: "12px",
    margin: "2px 0 0 0",
  },
  badge11: {
    background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    border: "1px solid #93c5fd",
    color: "#1d4ed8",
    fontSize: "11px",
    fontWeight: "700",
    padding: "4px 10px",
    borderRadius: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  headerTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginTop: "8px",
  },
  headerActions: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  refreshBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 14px",
    borderRadius: "8px",
    border: "1.5px solid #cbd5e1",
    background: "#fff",
    color: "#475569",
    fontWeight: "600",
    fontSize: "13px",
    cursor: "pointer",
  },

  /* LIGHT EXECUTIVE PAIRER STRIP WITH 4-SIDE BORDER */
  executiveBar: {
    background: "#ffffff",
    border: "1.5px solid #cbd5e1",
    borderRadius: "14px",
    padding: "16px 20px",
    marginBottom: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
    boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
    boxSizing: "border-box",
    width: "100%",
  },
  glowingIconBox: {
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    padding: "8px",
    borderRadius: "10px",
    display: "flex",
  },
  quickInputsWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
    width: "100%",
    boxSizing: "border-box",
  },
  quickPairBtn: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "10px 20px",
    borderRadius: "9px",
    border: "none",
    background: active ? "#2563eb" : "#cbd5e1",
    color: "#fff",
    fontWeight: "700",
    fontSize: "13px",
    cursor: active ? "pointer" : "not-allowed",
    boxShadow: active ? "0 2px 8px rgba(37,99,235,0.25)" : "none",
    transition: "all 0.15s ease",
    height: "44px",
    flex: "1 1 auto",
    minWidth: "140px",
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  }),

  tabGroup: {
    display: "flex",
    background: "#f1f5f9",
    padding: "3px",
    borderRadius: "9px",
    border: "1.5px solid #cbd5e1",
  },
  tabBtn: (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 12px",
    borderRadius: "7px",
    border: "none",
    background: active ? "#fff" : "transparent",
    color: active ? "#0f172a" : "#64748b",
    fontWeight: active ? "700" : "500",
    fontSize: "13px",
    cursor: "pointer",
    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
  }),
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
    gap: "12px",
    marginBottom: "20px",
  },
  statCard: {
    background: "#fff",
    border: "1.5px solid #cbd5e1",
    borderRadius: "12px",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
  },
  statIconBox: (color) => ({
    background: `${color}12`,
    padding: "9px",
    borderRadius: "10px",
    display: "flex",
  }),
  statValue: {
    fontSize: "20px",
    fontWeight: "800",
    color: "#0f172a",
  },
  statLabel: {
    fontSize: "11px",
    color: "#64748b",
  },
  errorAlert: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    padding: "10px 14px",
    borderRadius: "8px",
    fontSize: "13px",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  toolbar: {
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fff",
    border: "1.5px solid #cbd5e1",
    borderRadius: "8px",
    padding: "8px 12px",
    maxWidth: "380px",
    width: "100%",
  },
  searchInput: {
    border: "none",
    outline: "none",
    width: "100%",
    fontSize: "13px",
    color: "#0f172a",
  },
  selectedBanner: {
    background: "#eff6ff",
    border: "1px solid #93c5fd",
    color: "#1e40af",
    padding: "8px 14px",
    borderRadius: "8px",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  clearSelectedBtn: {
    background: "#dbeafe",
    border: "none",
    borderRadius: "4px",
    padding: "2px 8px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#1e40af",
    cursor: "pointer",
  },

  skeletonBar: {
    height: "14px",
    background: "#e2e8f0",
    borderRadius: "4px",
    width: "100px",
    animation: "pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite",
  },
  overlayLoader: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.72)",
    backdropFilter: "blur(2px)",
    zIndex: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: THEME,
    fontWeight: "600",
    fontSize: "13px",
    borderRadius: "16px",
  },
  /* RESPONSIVE FULL CANVAS BOARD WITH 4-SIDE UNIFORM BORDER */
  canvasBoard: (isMobile) => ({
    position: "relative",
    background: "#ffffff",
    border: "1.5px solid #cbd5e1",
    borderRadius: "16px",
    padding: isMobile ? "14px" : "24px",
    marginBottom: "24px",
    overflow: "visible",
    boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
  }),
  svgLayer: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: 1,
    overflow: "visible",
  },
  columnsGrid: (isMobile) => ({
    position: "relative",
    zIndex: 2,
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: isMobile ? "16px" : "180px",
    alignItems: "start",
  }),
  column: {
    background: "#f8fafc",
    border: "1.5px solid #cbd5e1",
    borderRadius: "14px",
    padding: "16px",
  },
  columnHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "14px",
    paddingBottom: "10px",
    borderBottom: "1px solid #e2e8f0",
  },
  columnHeaderBadge: (color) => ({
    background: `${color}15`,
    padding: "8px",
    borderRadius: "8px",
    display: "flex",
  }),
  columnTitle: {
    margin: 0,
    fontSize: "14px",
    fontWeight: "700",
    color: "#0f172a",
  },
  columnSubtitle: {
    fontSize: "11px",
    color: "#94a3b8",
  },
  nodesListFullHeight: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  docNodeCard: (selected, mapped, dragging) => ({
    position: "relative",
    background: selected ? "#eff6ff" : mapped ? "#faf5ff" : "#ffffff",
    border: selected
      ? "2px solid #2563eb"
      : mapped
        ? "1.5px solid #a855f7"
        : "1.5px solid #cbd5e1",
    borderRadius: "12px",
    padding: "12px 14px",
    cursor: "grab",
    opacity: dragging ? 0.4 : 1,
    transition: "all 0.15s ease",
    boxShadow: selected ? "0 4px 14px rgba(37,99,235,0.15)" : "0 1px 3px rgba(0,0,0,0.02)",
    userSelect: "none",
  }),
  roleNodeCard: (hovered, hasSelected, hasConnected) => ({
    position: "relative",
    background: hovered
      ? "#f0fdf4"
      : hasConnected
        ? "#eff6ff"
        : hasSelected
          ? "#f8fafc"
          : "#ffffff",
    border: hovered
      ? "2px dashed #10b981"
      : hasConnected
        ? "1.5px solid #2563eb"
        : hasSelected
          ? "2px dashed #2563eb"
          : "1.5px solid #cbd5e1",
    borderRadius: "12px",
    padding: "12px 14px",
    cursor: hasSelected ? "pointer" : "default",
    transition: "all 0.15s ease",
    boxShadow: hovered ? "0 4px 14px rgba(16,185,129,0.15)" : "0 1px 3px rgba(0,0,0,0.02)",
  }),
  handlePinRight: (mapped) => ({
    position: "absolute",
    right: "-6px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    background: mapped ? "#a855f7" : "#cbd5e1",
    border: "2px solid #fff",
    boxShadow: mapped ? "0 0 8px rgba(168,85,247,0.6)" : "none",
  }),
  handlePinLeft: (connected) => ({
    position: "absolute",
    left: "-6px",
    top: "50%",
    transform: "translateY(-50%)",
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    background: connected ? "#10b981" : "#cbd5e1",
    border: "2px solid #fff",
    boxShadow: connected ? "0 0 8px rgba(16,185,129,0.6)" : "none",
  }),
  nodeName: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#0f172a",
  },
  nodeCode: {
    fontSize: "11px",
    color: "#64748b",
    marginTop: "2px",
    fontFamily: "monospace",
  },
  docChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "8px",
    padding: "3px 8px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#1e40af",
  },
  chipRemoveBtn: {
    background: "none",
    border: "none",
    color: "#dc2626",
    fontWeight: "700",
    fontSize: "14px",
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
    marginLeft: "2px",
  },
  mappedBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    fontSize: "10px",
    fontWeight: "700",
    background: "#f3e8ff",
    color: "#7e22ce",
    padding: "2px 7px",
    borderRadius: "8px",
  },
  unmappedBadge: {
    fontSize: "10px",
    fontWeight: "600",
    background: "#f1f5f9",
    color: "#94a3b8",
    padding: "2px 7px",
    borderRadius: "8px",
  },
  roleCountBadge: (hasConnected) => ({
    fontSize: "11px",
    fontWeight: "700",
    background: hasConnected ? "#dbeafe" : "#f1f5f9",
    color: hasConnected ? "#1d4ed8" : "#94a3b8",
    padding: "3px 8px",
    borderRadius: "10px",
  }),
  connectedSingleDocBox: {
    marginTop: "10px",
    padding: "8px 10px",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  unassignedHint: {
    marginTop: "8px",
    fontSize: "11px",
    color: "#94a3b8",
    fontStyle: "italic",
  },
  tagUnlinkBtn: {
    background: "none",
    border: "none",
    color: "#dc2626",
    cursor: "pointer",
    padding: 0,
    display: "flex",
  },
  centerGuide: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    paddingTop: "100px",
  },
  pipelineBox: {
    background: "#ffffff",
    border: "1.5px solid #cbd5e1",
    borderRadius: "14px",
    padding: "16px",
    textAlign: "center",
    boxShadow: "0 4px 14px rgba(15, 23, 42, 0.05)",
  },

  /* EXECUTIVE ROLE CARDS GRID VIEW STYLES */
  cardsGridContainer: {
    marginBottom: "24px",
  },
  emptyCardsMsg: {
    padding: "40px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "14px",
    background: "#ffffff",
    borderRadius: "16px",
    border: "1.5px solid #cbd5e1",
  },
  rolesCardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
    gap: "20px",
    alignItems: "stretch",
  },
  roleGridCard: (hasConnected) => ({
    background: "#ffffff",
    border: hasConnected ? "1.5px solid #cbd5e1" : "1.5px solid #e2e8f0",
    borderRadius: "16px",
    padding: "18px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  }),
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
    paddingBottom: "14px",
    borderBottom: "1px solid #f1f5f9",
  },
  cardHeaderIconBox: (hasConnected) => ({
    background: hasConnected ? "#eff6ff" : "#f1f5f9",
    border: hasConnected ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
    padding: "9px",
    borderRadius: "10px",
    display: "flex",
  }),
  cardRoleTitle: {
    margin: 0,
    fontSize: "15px",
    fontWeight: "800",
    color: "#0f172a",
  },
  cardRoleCode: {
    fontSize: "11px",
    color: "#64748b",
    fontFamily: "monospace",
    marginTop: "2px",
  },
  cardBadgeActive: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "#ecfdf5",
    border: "1px solid #a7f3d0",
    color: "#047857",
    fontWeight: "700",
    fontSize: "11px",
    padding: "4px 9px",
    borderRadius: "12px",
  },
  cardBadgeUnmapped: {
    fontSize: "11px",
    fontWeight: "600",
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
    color: "#94a3b8",
    padding: "4px 9px",
    borderRadius: "12px",
  },
  cardBody: {
    padding: "14px 0",
    flex: 1,
  },
  cardSectionLabel: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#64748b",
    letterSpacing: "0.5px",
    marginBottom: "10px",
    textTransform: "uppercase",
  },
  cardChipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  },
  cardDocChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderRadius: "9px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: "600",
    color: "#1e40af",
    maxWidth: "100%",
  },
  cardEmptyDocsMsg: {
    fontSize: "12px",
    color: "#94a3b8",
    fontStyle: "italic",
    padding: "10px 0",
  },
  cardFooter: {
    paddingTop: "12px",
    borderTop: "1px solid #f1f5f9",
  },

  toastNotification: (type) => ({
    position: "fixed",
    top: "20px",
    right: "20px",
    zIndex: 99999,
    background: type === "success" ? "#065f46" : type === "info" ? "#1e40af" : "#991b1b",
    color: "#fff",
    padding: "12px 18px",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: "600",
    boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    animation: "fadeIn 0.3s ease",
  }),
};
